/**
 * Standardized game lifecycle events for all game types
 * 
 * These events enable games to be self-contained and interchangeable modules
 * that communicate with the platform through a consistent interface.
 * 
 * @example
 * ```typescript
 * // In GameEngine component
 * @Output() gameReady = new EventEmitter<void>();
 * @Output() gameStart = new EventEmitter<void>();
 * @Output() gameComplete = new EventEmitter<GameCompleteEvent>();
 * 
 * // In play.component.ts (platform)
 * <lib-game-engine
 *   (gameReady)="onGameReady()"
 *   (gameStart)="onGameStart()"
 *   (gameComplete)="onGameComplete($event)"
 * />
 * ```
 */

/**
 * Emitted when game is fully loaded and ready to play
 * Platform should show CTA button or enable user interaction
 */
export interface GameReadyEvent {
  // Currently void, but allows for future expansion
  // Could add: gameType, estimatedDuration, etc.
}

/**
 * Emitted when user initiates gameplay
 * Examples: Clicked spin button, started scratching, rolled dice
 */
export interface GameStartEvent {
  // Could add: triggeredBy ('button' | 'gesture' | 'auto')
  timestamp?: number;
}

/**
 * Optional progress updates during gameplay
 * Example: Scratchcard reveal progress, animation progress
 */
export interface GameProgressEvent {
  /**
   * Progress as decimal (0.0 - 1.0)
   * Example: 0.5 = 50% complete
   */
  percent: number;
  
  /**
   * Optional message describing current state
   * Example: "Scratching...", "Spinning...", "Calculating..."
   */
  message?: string;
}

/**
 * Emitted when game finishes and result is available
 * Platform handles prize reveal, confetti, navigation
 */
export interface GameCompleteEvent {
  /**
   * Index of winning prize/result in prizes array
   * Example: 0-based index, or -1 if no prize
   */
  resultIndex: number;
  
  /**
   * Full prize/result data object
   * Type depends on game (Prize, MatchResult, etc.)
   */
  resultData?: unknown;
  
  /**
   * Optional game-specific metadata
   * Example: spin duration, matches found, etc.
   */
  metadata?: Record<string, unknown>;
}

/**
 * Emitted when game encounters an error
 * Platform handles error display and recovery
 */
export interface GameErrorEvent {
  /**
   * Error code for programmatic handling
   * Examples: 'INIT_FAILED', 'RENDER_ERROR', 'CONFIG_INVALID'
   */
  code: string;
  
  /**
   * Human-readable error message
   */
  message: string;
  
  /**
   * Optional technical details for debugging
   */
  details?: unknown;
  
  /**
   * Whether error is recoverable
   * If true, user can retry. If false, must restart.
   */
  recoverable?: boolean;
}

/**
 * Union type of all game lifecycle events
 * Useful for event logging and analytics
 */
export type GameLifecycleEvent =
  | { type: 'ready'; data: GameReadyEvent }
  | { type: 'start'; data: GameStartEvent }
  | { type: 'progress'; data: GameProgressEvent }
  | { type: 'complete'; data: GameCompleteEvent }
  | { type: 'error'; data: GameErrorEvent };

/**
 * Event handler type aliases for convenience
 */
export type GameReadyHandler = (event: GameReadyEvent) => void;
export type GameStartHandler = (event: GameStartEvent) => void;
export type GameProgressHandler = (event: GameProgressEvent) => void;
export type GameCompleteHandler = (event: GameCompleteEvent) => void;
export type GameErrorHandler = (event: GameErrorEvent) => void;

/**
 * Interface for components that emit game lifecycle events
 * Games should implement this to ensure type safety
 */
export interface GameLifecycleEventEmitter {
  gameReady: { subscribe: (handler: GameReadyHandler) => void };
  gameStart: { subscribe: (handler: GameStartHandler) => void };
  gameProgress?: { subscribe: (handler: GameProgressHandler) => void };
  gameComplete: { subscribe: (handler: GameCompleteHandler) => void };
  gameError?: { subscribe: (handler: GameErrorHandler) => void };
}
